import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Box, ThemeProvider, useMediaQuery } from '@mui/material';
import { DarkTheme, LightTheme } from './../themes';
import { LogoDark, LogoLight } from '../assets';
import { ThemePreference } from '../constants/themePreference';
import { userService } from '../services/api/userService';
import { Api } from '../services/api/axios-config';
import { setCsrfRequestToken } from '../services/api/axios-config/interceptors';

const THEME_STORAGE_KEY = 'APP_THEME';

interface IThemeContextData {
  themeName: 'light' | 'dark';
  toggleTheme: () => void;
  logo: string;
  setThemeFromPreference: (pref: ThemePreference) => void;
  isMobile: boolean;
  isTablet: boolean;
  lgDown: boolean;
}

const ThemeContext = createContext({} as IThemeContextData);

export const useAppThemeContext = () => {
  return useContext(ThemeContext);
};

const getInitialTheme = (): 'light' | 'dark' => {
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return 'dark';
};

type Props = {
  children?: React.ReactNode,
};

export const AppThemeProvider: React.FC<Props> = ({ children }) => {
  const [themeName, setThemeName] = useState<'light' | 'dark'>(getInitialTheme);

  const logo = useMemo(() => themeName === 'dark' ? LogoDark : LogoLight, [themeName]);

  const setThemeFromPreference = useCallback((pref: ThemePreference) => {
    const newTheme = pref === ThemePreference.Light ? 'light' : 'dark';
    setThemeName(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeName(oldThemeName => {
      const newTheme = oldThemeName === 'light' ? 'dark' : 'light';
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);

      // Fire-and-forget API sync — refresh CSRF token first in case it's stale
      const themePref = newTheme === 'light' ? ThemePreference.Light : ThemePreference.Dark;
      Api.get('/auth/antiforgery-token').then((res) => {
        if (res.data) setCsrfRequestToken(res.data as string);
      }).catch(() => undefined).finally(() => {
        userService.updateTheme(themePref).catch((err) => {
          if (err?.response?.status !== 401 && err?.response?.status !== 403) {
            console.warn('Theme sync failed:', err);
          }
        });
      });

      return newTheme;
    });
  }, []);

  const theme = useMemo(() => {
    return themeName === 'light' ? LightTheme : DarkTheme;
  }, [themeName]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const lgDown = useMediaQuery(theme.breakpoints.down('lg'));

  return (
    <ThemeContext.Provider value={{ themeName, toggleTheme, logo, setThemeFromPreference, isMobile, isTablet, lgDown }}>
      <ThemeProvider theme={theme}>
        <Box width='100vw' height='100vh' bgcolor={theme.palette.background.default}>
          {children}
        </Box>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
